import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import "../facilitiesMaster/facilitiesMaster.scss";
import DeleteModal from "../../components/common/DeleteModal";
import SaveSuccessModel from "../../components/common/SaveSuccessModel";
import FetchData, { DeleteEntry, GetList } from "../../providers/dataProvider";
import { CheckNull } from "../../utils/Validate";
import { SearchComponent } from "../../components/common/SearchComponent";
import ErrorMessage from "../../components/common/ErrorMessage";
import FacilitiesGrid from "../../components/FacilitiesMaster/CompanyGrid";
import { SuccessMessages, ErrorMessages } from "../../constants/MessageConstants";
import DataSearch from "../../utils/DataSearch";
import { useRolePermission } from "../../hooks/useRolePermission";

const FacilitiesMasterList = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const userProfile = JSON.parse(localStorage.getItem("userProfile"));
  const companyId = userProfile?.userCompanyId || "";

  const [search, setSearch] = useState("");
  const [refreshSearch, setRefreshSearch] = useState(false);
  const [facilityData, setFacilityData] = useState([]);
  const [filteredResults, setFilteredResults] = useState([]);
  const [deleteShow, setDeleteShow] = useState({
    show: false,
    item: "",
    title: "",
    textCode: "",
    text: "",
    loading: false,
  });
  const [createMsg, setCreateMsg] = useState(null);
  const [successShow, setSuccessShow] = useState(false);
  const [refreshData, setRefreshData] = useState([]);
  const [errorShow, setErrorShow] = useState({
    show: false,
    errorMessage: "",
  });
   const { menuRights } = useRolePermission("Facility Master");

  const baseColumns = [
    { heading: "Facility Code", value: "facilityCode", sortable: true },
    { heading: "Facility Name", value: "facilityName", sortable: true },
    { heading: "Country", value: "countryName", sortable: true },
    { heading: "Address", value: "address", sortable: true },
    { heading: "Default", value: "isDefaultLabel", sortable: true },
    { heading: "Status", value: "status", sortable: true },
  ];

  const {
    data: countrydata,
    loading: countryLoading,
    error: countryError,
  } = FetchData("country");
  const { data, loading, error } = FetchData("manufacturingFacility", {
    companyId,
  });

  useEffect(() => {
    if (!data) return;

    const list = Array.isArray(data) ? data : data?.data || [];
    const countries = Array.isArray(countrydata) ? countrydata : [];
    const withLookups = list.map((item) => {
      const countryValue = countries.find((country) => country?.id === item?.countryId);
      return {
        ...item,
        countryName: countryValue?.name || "",
        status: item?.isActive === true ? "Active" : "Inactive",
        isDefaultLabel: item?.isDefault ? "Yes" : "No",
      };
    });

    setFacilityData(withLookups);
    setFilteredResults([...withLookups].sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt)));
  }, [data, countrydata]);

  useEffect(() => {
    if (refreshData?.message === "success") {
      const list = Array.isArray(refreshData?.data)
        ? refreshData?.data
        : refreshData?.data?.data || [];
      setFacilityData(list);
    }
  }, [refreshData]);

  const handleEdit = (item) => {
    navigate("/add-new-facility", {
      state: {
        row: item,
        id: item?.id,
        companyId,
        companyName: location?.state?.companyName || "",
        canAdd: true,
        canEdit: true,
      },
    });
  };

  const handleView = (item) => {
    navigate("/view-facility", {
      state: {
        ...item,
        companyId,
        companyName: location?.state?.companyName || "",
      },
    });
  };

  const addNew = () => {
    navigate("/add-new-facility", {
      state: {
        companyId,
        companyName: location?.state?.companyName || "",
        canAdd: true,
        canEdit: true,
      },
    });
  };

  const handleDelete = (item) => {
    setDeleteShow({
      show: true,
      item,
      title: "You want delete this facility?",
      textCode: "Facility Name",
      text: item?.facilityName,
      loading: false,
    });
  };

  const HandleCancelDelete = () => {
    setDeleteShow({
      show: false,
      item: "",
      title: "",
      textCode: "",
      text: "",
      loading: false,
    });
  };

  const HandleConfirmDelete = (item) => {
    setDeleteShow((prev) => ({ ...prev, loading: true }));
    DeleteEntry(
      "manufacturingFacility",
      { ...item, companyId },
      setCreateMsg,
      SuccessMessages.DELETED("Facility"),
      ErrorMessages.DELETED_ERROR("Facility")
    );
  };

  useEffect(() => {
    if (!createMsg?.message) return;

    if (createMsg?.successData === "Successful") {
      setDeleteShow({
        show: false,
        item: "",
        title: "",
        textCode: "",
        text: "",
        loading: false,
      });
      setSuccessShow(true);
      return;
    }

    if (CheckNull(createMsg?.message) !== "") {
      setDeleteShow({
        show: false,
        item: "",
        title: "",
        textCode: "",
        text: "",
        loading: false,
      });
      setErrorShow({ show: true, errorMessage: createMsg?.successData });
    }
  }, [createMsg]);

  const SuccessDoneClick = () => {
    setSuccessShow(false);
    GetList("manufacturingFacility", { companyId }, setRefreshData);
  };

  const ErrorDoneClick = () => {
    setErrorShow({
      show: false,
      errorMessage: "",
    });
    setCreateMsg(null);
  };

  const HandleKeyDown = (event) => {
    if (event.key === "Enter") {
      setFilteredResults(
        DataSearch(
          facilityData,
          search,
          baseColumns.filter(({ value }) => !value.includes("status"))
        )
      );
      setRefreshSearch(false);
    }
  };

  const ApplySearch = () => {
    const SearchResult = DataSearch(
      facilityData,
      search,
      baseColumns.filter(({ value }) => !value.includes("status"))
    );
    setFilteredResults(SearchResult);
    setRefreshSearch(false);
  };

  useEffect(() => {
    if (refreshSearch === true) ApplySearch();
  }, [search]);

  const RemoveSearch = () => {
    setSearch("");
    setRefreshSearch(true);
  };

  const RefreshList = () => {
    GetList("manufacturingFacility", { companyId }, setRefreshData);
  };

  useEffect(() => {
    if (refreshData?.message === "success") {
      const list = Array.isArray(refreshData?.data)
        ? refreshData?.data
        : refreshData?.data?.data || [];
      const countries = Array.isArray(countrydata) ? countrydata : [];
      const withLookups = list.map((item) => {
        const countryValue = countries.find((country) => country?.id === item?.countryId);
        return {
          ...item,
          countryName: countryValue?.name || "",
          status: item?.isActive === true ? "Active" : "Inactive",
          isDefaultLabel: item?.isDefault ? "Yes" : "No",
        };
      });
      setFacilityData(withLookups);
      setFilteredResults([...withLookups].sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt)));
    }
  }, [refreshData, countrydata]);

  return (
    <>
      <section className="company-master-list-sec" id="facilitiesMasterList">
        <div className="container-fluid">
          <div className="row">
            <div className="col-xxl-12 col-xl-12 col-lg-12 col-md-12 col-sm-12 col-xs-12 px-0 py-1">
              <div className="card">
                <div className="card-body">
                  <div className="row search-area-row">
                    <div className="col-xxl-6 col-xl-6 col-lg-6 col-md-4 col-sm-12 col-xs-12 my-auto p-0">
                      <div>
                        <p>{filteredResults?.length} Results Found</p>
                      </div>
                    </div>
                    <div className="col-xxl-6 col-xl-6 col-lg-6 col-md-8 col-sm-12 col-xs-12 my-auto p-0">
                      <SearchComponent
                        filteredResults={filteredResults}
                        search={search}
                        handleKeyDown={HandleKeyDown}
                        setSearch={setSearch}
                        removeSearch={RemoveSearch}
                        ApplySearch={ApplySearch}
                        RefreshList={RefreshList}
                        baseColumns={baseColumns}
                        data={facilityData}
                        placeholder="Search by Facility"
                        addNew={addNew}
                        menuRights={menuRights}
                        HideFilter="HideFilter"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <FacilitiesGrid
            filteredResults={filteredResults}
            handleEdit={handleEdit}
            handleDelete={handleDelete}
            handleView={handleView}
            loading={loading}
            menuRights={menuRights}
            moduleName="manufacturingFacility"
          />
        </div>
      </section>

      <div className="container-fluid company-master-list-sec pl-5 pr-5">
        <DeleteModal
          deleteShow={deleteShow}
          handleCancelDelete={HandleCancelDelete}
          handleConfirmDelete={HandleConfirmDelete}
        />
        <ErrorMessage
          Show={errorShow?.show}
          errorMessage={errorShow?.errorMessage}
          ErrorDoneClick={ErrorDoneClick}
        />
        <SaveSuccessModel
          show={successShow}
          createMsg={createMsg}
          setShow={setSuccessShow}
          SuccessDoneClick={SuccessDoneClick}
        />
      </div>
    </>
  );
};

export default FacilitiesMasterList;
